import Api from '@/providers/Api'
import User from '../apps/default/User'
import SurveyNote from '@/providers/SurveyNote'
import router from '../apps/default/router'
import * as _ from 'lodash'

/**
 * A simple authorization service for the router, as well as for general use throughout the app.
 *
 * In the router, each route that has authorization restrictions should provide a security rule
 * as part of its meta, e.g.:
 *
 *      meta: {
 *          ...
 *          security: (auth) => auth.roleIs('ADMIN')
 *      }
 *
 * The security function can use any of the helper functions defined on this service
 * (e.g. to AND/OR conditions); if it returns false, navigation is aborted.
 *
 * If a router security rule requires access to a route param, such as a participant ID,
 * it must access it through the auth.to variable, which is provided for convenience - e.g.:
 *
 *     security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId)
 *
 * When used in the router, helper functions that require async calls to the API will
 * initially allow navigation, but trigger an error and undo navigation if the API call
 * comes back negative.
 *
 * Authorization methods or helpers can also be used independently of the router, e.g. to
 * show/hide or enable/disable links or buttons. If an authorization method requires access
 * to a complex object, such as a SurveyNote, that is not readily accessible from the current
 * user state, it must declare that object as an argument, and it will generally not be
 * suitable for use with the router.
 */

const pRoles = User.PRACTITIONER_ROLES

export default {
    // params (make these object-level so we don't have to pass them around in function calls)
    to: null,
    from: null,
    next: null,

    // helper functions (application-specific)

    roleIs(role) {
        return User.roleIs(role)
    },

    isMaskingAdmin() {
        return User.isMaskingAdmin()
    },

    isCompanyAdminFor(companyId) {
        return User.roleIsCompanyAdmin() && User.adminCompany().id == companyId
    },

    isResellerAdminFor(resellerId) {
        return User.roleIsResellerAdmin() && User.reseller().id == resellerId
    },

    async isResellerAdminForCompany(companyId) {
        Api.getItem({},'company', companyId).then((company) => {
            return User.reseller().id == company.reseller_id
        })
    },

    isPractitionerFromSamePractice(practiceId) {
        return User.practitionerPracticeId() == practiceId
    },

    isProviderForParticipant(participantId) {
        // need to check asynchronously - so let it go, then throw an error if the check fails
        Api.findProvider(null, User.practitionerPracticeId(), participantId)
            .then((provider) => {
                if (!provider) {
                    this.authError('Unauthorized')
                }
            })
        return true
    },

    // display an error message and block/undo navigation

    authError(msg) {
        router.app.$toasted.error(msg, {duration: 5000})
        this.next(this.from)
    },

    // -------------- permissions for Practitioners --------------

    canManagePatients() {
        // Manage patients
        // Send invitations
        // Set appointment dates
        // Manage bio/anthro data
        return this.roleIs('PRACTITIONER')
    },

    canManagePracticeResources() {
        // practice-specific plans (??)
        // plan resources
        // invitation letters
        // handouts
        return this.isPracticeManager() || this.isSoloPhysicianOrCoach()
    },

    canManageCompanyPlans() {
        return this.canManagePracticeResources() && User.currentPractice().isOnlyPracticeForCompany
    },

    canManagePracticeStaff() {
        // practitioners
        // appointment alerts
        // practice details
        // analytic features (TBD)
        return this.isPracticeManager() || this.isSoloPhysicianOrCoach()
    },

    canManagePracticeBilling() {
        // manage superbills
        // download Form 1500
        return this.practitionerRoleIsOneOf([
            pRoles.PHYSICIAN,
            pRoles.MEDICAL_SPECIALIST,
            pRoles.NURSE_PRACTITIONER,
            pRoles.PHYSICIAN_ASSISTANT,
            pRoles.NURSE_SPECIALIST,
            pRoles.NURSE,
            pRoles.PRACTICE_MANAGER,
            pRoles.HEALTH_MENTOR,
            pRoles.WELLNESS_COACH,
            pRoles.DIETITIAN,
            pRoles.EXERCISE_SPECIALIST
        ])
        // NB: not MEDICAL_ASSISTANT
    },

    canManageAppointments() {
        return true  //TODO: add practitioner types or remove
    },

    canEditWellnessPlan() {
        // habits, goals, vision, logs targets
        return this.practitionerRoleIsOneOf([
            pRoles.PHYSICIAN,
            pRoles.MEDICAL_SPECIALIST,
            pRoles.NURSE_PRACTITIONER,
            pRoles.PHYSICIAN_ASSISTANT,
            pRoles.HEALTH_MENTOR,
            pRoles.WELLNESS_COACH,
            pRoles.DIETITIAN,
            pRoles.EXERCISE_SPECIALIST
        ])
    },

    canCreateDraftSoapNote(noteType) {
        const roleOK = this.practitionerRoleIsOneOf([
            pRoles.PHYSICIAN,
            pRoles.MEDICAL_SPECIALIST,
            pRoles.NURSE_PRACTITIONER,
            pRoles.PHYSICIAN_ASSISTANT,
            pRoles.NURSE_SPECIALIST,
            pRoles.NURSE,
            pRoles.MEDICAL_ASSISTANT,
            // practice manager?
            pRoles.HEALTH_MENTOR,
            pRoles.WELLNESS_COACH,
            pRoles.DIETITIAN,
            pRoles.EXERCISE_SPECIALIST
        ])

        if (noteType == SurveyNote.NOTE_TYPES.AWV) {
            return roleOK &&
                (this.hasPrimaryCarePractice() || this.hasCoachingPractice()) &&
                !User.practitionerRoleIs(pRoles.MEDICAL_SPECIALIST)
        } else if (noteType == SurveyNote.NOTE_TYPES.CCM) {
            return roleOK &&
                this.hasPrimaryCarePractice()
        } else {
            return roleOK
        }
    },

    canDeleteDraftSoapNote(noteType) {
        return this.canCreateDraftSoapNote(noteType)
    },

    // NB: not suitable for use with router
    canLockSoapNote(note) {
        const roleOK = this.practitionerRoleIsOneOf([
            pRoles.PHYSICIAN,
            pRoles.MEDICAL_SPECIALIST,
            pRoles.NURSE_PRACTITIONER,
            pRoles.PHYSICIAN_ASSISTANT,
            pRoles.NURSE_SPECIALIST,
            pRoles.NURSE,
            pRoles.HEALTH_MENTOR,
            pRoles.WELLNESS_COACH,
            pRoles.DIETITIAN,
            pRoles.EXERCISE_SPECIALIST
        ])

        const creatorOK = this.noteCreatorIsSelfOrSubordinatePractitioner(note)

        if (!roleOK || !creatorOK) {
            return false
        } else {
            if (note.note_type == SurveyNote.NOTE_TYPES.AWV || note.note_type == SurveyNote.NOTE_TYPES.CCM) {
                return (this.hasPrimaryCarePractice() || this.hasCoachingPractice()) &&
                    !User.practitionerRoleIs(pRoles.MEDICAL_SPECIALIST)
            } else if (note.note_type == SurveyNote.NOTE_TYPES.COACHING) {
                return !this.practitionerRoleIsOneOf([
                    pRoles.NURSE,
                    pRoles.HEALTH_MENTOR,
                    pRoles.DIETITIAN,
                ])
            } else {
                return true
            }
        }
    },

    // helpers

    practitionerRoleIsOneOf(roles) {
        return null != _.find(roles, (r) => {
            return User.practitionerRoleIs(r)
        })
    },
    isPracticeManager() {
        return User.practitionerRoleIs(pRoles.PRACTICE_MANAGER)
    },
    isSoloPhysicianOrCoach() {
        return (User.practitionerRoleIs(pRoles.PHYSICIAN) ||
            User.practitionerRoleIs(pRoles.MEDICAL_SPECIALIST) ||
            User.practitionerRoleIs(pRoles.WELLNESS_COACH)) &&
            User.practitionerPracticeLacksPracticeManager()
    },

    hasPrimaryCarePractice() {
        return User.practitionerPracticeType().indexOf('PRIMARY') != -1
    },
    hasCoachingPractice() {
        return User.practitionerPracticeType().indexOf('COACH') != -1
    },
    hasSpecialistPractice() {
        return User.practitionerPracticeType().indexOf('SPECIALIST') != -1
    },

    // NB: not suitable for use with router
    noteCreatorIsSelfOrSubordinatePractitioner(note) {
        const userIsCreator = note.practitioner_id == User.practitionerId()

        const creatorLevel = this.getPractitionerLevel(note.practitioner_role)
        const userLevel = this.getPractitionerLevel(User.practitionerRole())

        return userIsCreator || userLevel >= creatorLevel
    },

    getPractitionerLevel(role) {
        if (role == pRoles.PHYSICIAN || role == pRoles.MEDICAL_SPECIALIST) {
            return 5
        } else if (role == pRoles.NURSE_PRACTITIONER) {
            return 4
        } else if (role == pRoles.PHYSICIAN_ASSISTANT) {
            return 3
        } else if (role == pRoles.NURSE_SPECIALIST) {
            return 2
        } else {
            return 1
        }
    }


}
